
        // ===== Reusable list editor for intel-packet sections =====
        // Each "item" is an object with the fields specified in `fields` prop.
        // Renders a stack of rows with a small remove button per row, and
        // a single "+ Add" button to append a fresh empty item.
        //
        // Field types supported:
        //   text (default)  - single-line input
        //   multiline       - textarea
        //   dropdown        - select from options=[{code,label}]
        //
        // Optional suggestion chips:
        //   pass chips=['Phrase A','Phrase B'] and chipFieldKey='description'
        //   to render a chip strip above the list. Clicking a chip appends
        //   a fresh item with the chip's text in the named field.
        function IntelListEditor({ title, helpText, items, setItems, fields, chips, chipFieldKey, chipHint }) {
            const addItem = (prefill) => {
                const blank = {};
                fields.forEach(f => { blank[f.key] = ''; });
                if (prefill) Object.assign(blank, prefill);
                setItems([...items, blank]);
            };
            const updateItem = (idx, key, value) => {
                const next = items.map((it, i) => i === idx ? { ...it, [key]: value } : it);
                setItems(next);
            };
            const removeItem = (idx) => {
                setItems(items.filter((_, i) => i !== idx));
            };

            const renderField = (item, idx, f) => {
                const v = item[f.key] || '';
                if (f.type === 'dropdown') {
                    return (
                        <select value={v}
                            onChange={e => updateItem(idx, f.key, e.target.value)}>
                            <option value="">— select —</option>
                            {(f.options || []).map(opt => (
                                <option key={opt.code} value={opt.code}>{opt.label}</option>
                            ))}
                        </select>
                    );
                }
                if (f.multiline) {
                    return (
                        <textarea rows="2" value={v}
                            onChange={e => updateItem(idx, f.key, e.target.value)}
                            placeholder={f.placeholder} />
                    );
                }
                return (
                    <input type="text" value={v}
                        onChange={e => updateItem(idx, f.key, e.target.value)}
                        placeholder={f.placeholder} />
                );
            };

            return (
                <div>
                    <div className="font-mono text-xs uppercase tracking-widest gold mb-1">{title}</div>
                    {helpText && <div className="text-xs text-muted mb-3">{helpText}</div>}

                    {chips && chips.length > 0 && (
                        <div className="mb-3">
                            <div className="flex flex-wrap gap-2">
                                {chips.map(c => (
                                    <button key={c} type="button"
                                        onClick={() => addItem({ [chipFieldKey]: c })}
                                        className="text-xs px-3 py-1"
                                        style={{
                                            background: 'rgba(201,169,110,0.06)',
                                            border: '1px solid var(--veil)',
                                            color: 'var(--paper-muted)',
                                            cursor: 'pointer',
                                        }}>
                                        + {c}
                                    </button>
                                ))}
                            </div>
                            {chipHint && (
                                <div className="text-xs text-dim mt-2 italic">{chipHint}</div>
                            )}
                        </div>
                    )}

                    {items.length === 0 ? (
                        <div className="text-xs text-dim italic mb-2">None added yet.</div>
                    ) : (
                        <div className="space-y-3 mb-3">
                            {items.map((item, idx) => (
                                <div key={idx} className="p-3" style={{background: 'var(--zone-2)', border: '1px solid var(--veil)'}}>
                                    <div className="flex justify-between items-start gap-3 mb-2">
                                        <div className="font-mono text-xs text-muted">#{idx + 1}</div>
                                        <button type="button" onClick={() => removeItem(idx)}
                                            className="text-xs text-muted hover:text-[var(--red)] uppercase tracking-widest">
                                            Remove
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {fields.map(f => (
                                            <div key={f.key} className={f.multiline ? 'md:col-span-2' : ''}>
                                                <label className="text-xs">{f.label}{f.required && ' *'}</label>
                                                {renderField(item, idx, f)}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <button type="button" className="btn-ghost" onClick={() => addItem()}
                        style={{fontSize: '11px', padding: '5px 12px'}}>
                        + Add
                    </button>
                </div>
            );
        }

export { IntelListEditor };
